
import { supabase } from '@/integrations/supabase/client';
import { Lead, Activity } from '@/types/lead';
import { LeadScore, LeadScoreBreakdown } from '@/types/leadScore';
import { getLeadById } from '../queries/getLeadById';
import { getActivitiesByLead } from '../activityService';
import { calculateScoreBreakdown } from './componentScores';

// Calculate and save a lead score
// Accepts either leadId (will fetch data) or Lead object + activities (uses existing data)
export const calculateAndSaveLeadScore = async (
  leadIdOrLead: string | Lead,
  activitiesParam?: Activity[]
): Promise<LeadScore | null> => {
  try {
    // If string passed, fetch data (backward compatibility)
    const lead = typeof leadIdOrLead === 'string' 
      ? await getLeadById(leadIdOrLead)
      : leadIdOrLead;
      
    if (!lead) {
      console.error('Lead not found for scoring');
      return null;
    }

    // Use provided activities or fetch if not provided
    const activities = activitiesParam ?? await getActivitiesByLead(lead.id);

    // Ensure we have valid data structures
    const validLead = {
      ...lead,
      notes: lead.notes || [],
      documents: lead.documents || [],
      keyContacts: lead.keyContacts || []
    };

    // Calculate the breakdown score with safe data
    const scoreBreakdown = calculateScoreBreakdown(validLead, activities || []);

    // Prepare score data for saving
    const scoreData = {
      lead_id: lead.id,
      total_score: scoreBreakdown.totalScore,
      demographic_score: scoreBreakdown.scoreDetails.demographic.score,
      engagement_score: scoreBreakdown.scoreDetails.engagement.score,
      activity_score: scoreBreakdown.scoreDetails.activity.score,
      recency_score: scoreBreakdown.scoreDetails.recency.score,
      last_calculated: new Date().toISOString()
    };

    // Use upsert operation with proper TypeScript syntax
    // Note: removed 'returning' as it's not a valid option according to the error
    const { data, error } = await supabase
      .from('lead_scores')
      .upsert(scoreData, {
        onConflict: 'lead_id'
      })
      .select()
      .single();
      
    if (error) throw error;

    return {
      id: data.id,
      leadId: data.lead_id,
      totalScore: data.total_score,
      demographicScore: data.demographic_score,
      engagementScore: data.engagement_score,
      activityScore: data.activity_score,
      recencyScore: data.recency_score,
      lastCalculated: data.last_calculated,
      createdAt: data.created_at,
      updatedAt: data.updated_at
    };
  } catch (error) {
    console.error('Error calculating and saving lead score:', error);
    return null;
  }
};

// Get the detailed score breakdown for visualization
export const getLeadScoreBreakdown = async (leadId: string): Promise<LeadScoreBreakdown | null> => {
  try {
    const lead = await getLeadById(leadId);
    if (!lead) return null;
    
    const activities = await getActivitiesByLead(leadId);
    return calculateScoreBreakdown(lead, activities);
  } catch (error) {
    console.error('Error getting score breakdown:', error);
    return null;
  }
};
